import { QueryKey, useQueryClient } from "@tanstack/react-query";

/**
 * Standard query key factory for consistent key generation
 * This ensures all your query keys follow the same pattern
 */
export const queryKeys = {
  // Top-level keys for internal app
  experiments: (brandId?: string | number) =>
    brandId ? (["experiments", brandId] as const) : (["experiments"] as const),
  experiment: (id: string | number) => ["experiments", id] as const,
  experimentCreatives: (experimentId: string | number) => ["experiments", experimentId, "creatives"] as const,

  brands: () => ["brands"] as const,
  brand: (id: string | number) => ["brands", id] as const,

  users: (brandId?: string | number) => (brandId ? (["users", brandId] as const) : (["users"] as const)),
  user: (id: string | number) => ["users", id] as const,

  adAssetGroups: (brandId?: string | number) =>
    brandId ? (["ad-asset-groups", brandId] as const) : (["ad-asset-groups"] as const),
  adAssetGroup: (id: string | number) => ["ad-asset-groups", id] as const,

  adAssets: (groupId?: string | number) => (groupId ? (["ad-assets", groupId] as const) : (["ad-assets"] as const)),

  creativeEval: (params?: object | null) =>
    params ? (["creative-eval", params] as const) : (["creative-eval"] as const),

  tokenWallet: (brandId: string | number) => ["token-wallet", brandId] as const
} as const;

/**
 * Utility to invalidate multiple related queries at once
 * Example: invalidateQueries(queryClient, [queryKeys.experiments(brandId), queryKeys.brands()])
 */
export function invalidateQueries(queryClient: ReturnType<typeof useQueryClient>, queryKeys: QueryKey[]) {
  return Promise.all(queryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key })));
}

/**
 * Utility to prefetch multiple queries
 * Useful for preloading data on route changes or user interactions
 */
export function prefetchQueries<T = unknown>(
  queryClient: ReturnType<typeof useQueryClient>,
  queries: Array<{
    queryKey: QueryKey;
    queryFn: () => Promise<T>;
    staleTime?: number;
  }>
) {
  return Promise.all(
    queries.map(({ queryKey, queryFn, staleTime }) =>
      queryClient.prefetchQuery({
        queryKey,
        queryFn,
        staleTime: staleTime ?? 5 * 60 * 1000 // 5 minutes default
      })
    )
  );
}

/**
 * Common query options that can be reused across your app
 */
export const commonQueryOptions = {
  // For data that changes frequently (like live experiments)
  realtime: {
    staleTime: 0,
    gcTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000 // 30 seconds
  },

  // For relatively stable data (like experiments, users)
  stable: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  },

  // For data that rarely changes (like brands)
  static: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000 // 1 hour
  }
} as const;
